import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import { parseThemeEvent, type DittoTheme } from '@/lib/dittoTheme';

/**
 * Fetch a profile's active Ditto theme (kind 16767, replaceable).
 * Returns null when the user has no active theme.
 */
export function useProfileTheme(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['profileTheme', pubkey],
    queryFn: async ({ signal }) => {
      if (!pubkey) return null;

      const events = await nostr.query(
        [{ kinds: [16767], authors: [pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(6000)]) },
      );

      if (events.length === 0) return null;
      return parseThemeEvent(events[0]);
    },
    enabled: !!pubkey,
    staleTime: 5 * 60 * 1000,
  });
}

export interface ThemeDefinition extends DittoTheme {
  /** Pubkey of the theme author */
  pubkey: string;
  /** d-tag slug */
  dTag: string;
  /** description tag */
  description?: string;
  /** naddr identifier for this theme definition */
  naddr: string;
}

/**
 * Fetch publicly shared Ditto theme definitions (kind 36767, addressable).
 */
export function useThemeDefinitions() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['themeDefinitions'],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [36767], limit: 100 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );

      // Keep only the newest version of each addressable theme
      const byAddress = new Map<string, typeof events[number]>();
      for (const event of events) {
        const dTag = event.tags.find(t => t[0] === 'd')?.[1];
        if (!dTag) continue;
        const key = `${event.pubkey}:${dTag}`;
        const existing = byAddress.get(key);
        if (!existing || event.created_at > existing.created_at) {
          byAddress.set(key, event);
        }
      }

      const themes: ThemeDefinition[] = [];
      for (const event of byAddress.values()) {
        const parsed = parseThemeEvent(event);
        if (!parsed) continue;

        const dTag = event.tags.find(t => t[0] === 'd')?.[1] as string;
        const description = event.tags.find(t => t[0] === 'description')?.[1];

        themes.push({
          ...parsed,
          pubkey: event.pubkey,
          dTag,
          description,
          naddr: nip19.naddrEncode({ kind: 36767, pubkey: event.pubkey, identifier: dTag }),
        });
      }

      // Alphabetical by title for a predictable picker
      return themes.sort((a, b) => (a.title ?? a.dTag).localeCompare(b.title ?? b.dTag));
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a Ditto theme definition (kind 36767, addressable) by naddr.
 * Used for the app-wide theme selected in Settings.
 */
export function useThemeDefinition(naddr: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['themeDefinition', naddr],
    queryFn: async ({ signal }): Promise<DittoTheme | null> => {
      if (!naddr) return null;

      let decoded: nip19.DecodeResult;
      try {
        decoded = nip19.decode(naddr);
      } catch {
        return null;
      }
      if (decoded.type !== 'naddr') return null;
      const { kind, pubkey, identifier } = decoded.data;

      const events = await nostr.query(
        [{ kinds: [kind], authors: [pubkey], '#d': [identifier], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(6000)]) },
      );

      if (events.length === 0) return null;
      return parseThemeEvent(events[0]);
    },
    enabled: !!naddr,
    staleTime: 10 * 60 * 1000,
  });
}
